import type { Strictness } from "~/components/GameSettings";

const normalize = (value: string, strictness?: Strictness) => {
  const normalized = value
    .replace(
      // replace all types of single quotes with a straight single quote
      /[‘’‚‛]/g,
      "'",
    )
    .replace(
      // replace all types of double quotes with a straight double quote
      /[“”„‟]/g,
      '"',
    )
    .replace(/…/g, "...") // replace ellipses with three dots
    .replace(/—|–/g, "-"); // replace em-dashes and en-dashes with a hyphen
  return strictness?.cap ? normalized : normalized.toLowerCase();
};

const compareLetters = (a: string, b: string, strictness?: Strictness) => {
  if (a === b) {
    // first check for an exact match
    return true;
  }

  if (strictness?.acc) {
    // if strict accents are enabled, fail the check here
    return false;
  }

  // otherwise, remove accents from the solution and check again
  const unaccented = b
    .normalize(
      // break down accented characters into their base character and separate
      // accent
      "NFD",
    )
    .replace(
      // target the Unicode range that represents diacritical marks (accents)
      // and remove them
      /[\u0300-\u036f]/g,
      "",
    );

  // we don't allow the wrong accent to be used in place of the correct one, though
  return a === unaccented;
};

type Match = (boolean | string)[];
type Matches = Match[];

export const getMatches = (
  guess: string,
  solution: string,
  strictness?: Strictness,
): Matches => {
  const matches: Matches = [[]];
  const exceptions: string[] = [];

  const normalizedGuess = normalize(guess, strictness);
  const normalizedSolution = normalize(solution, strictness);

  const letters = normalizedSolution
    .trim() // remove leading and trailing spaces
    .replace(/\s+/g, " ") // remove extra spaces
    .split("");

  for (let i = 0; i < letters.length; i++) {
    const letter = letters[i];

    if (!letter) {
      break;
    }

    const guessLetter = normalizedGuess.charAt(i - exceptions.length);
    const isMatch = strictness?.acc
      ? guessLetter === letter
      : compareLetters(guessLetter, letter, strictness);
    const isSpace = /\s/.test(letter);

    if (isSpace) {
      // if the letter is a space, we need to add a new match
      matches.push([]);
    }

    if (isMatch) {
      if (!isSpace) {
        matches[matches.length - 1]?.push(true);
      }
      continue;
    }

    // check if the letter is a punctuation mark or a space
    // if it is, we don't test it against the guess
    const isException = /[\p{P}\s]/u.test(letter) && !strictness?.pun;

    if (!guessLetter) {
      if (!isException && !isSpace) {
        matches[matches.length - 1]?.push(letter);
      }
      continue;
    }

    if (isException) {
      // accept spaces in place of hyphens
      if (letter === "-" && /\s/.test(guessLetter)) {
        matches[matches.length - 1]?.push(true);
        continue;
      }

      exceptions.push(letter);
      continue;
    }

    matches[matches.length - 1]?.push(false);
  }

  return matches;
};

/**
 * Count the number of correct letters in a guess. This is used to determine the
 * solution (or alternate) that best fits the current guess.
 */
const countMatches = (matches: Matches) => {
  let count = 0;
  for (const match of matches.flat()) {
    if (match === true) {
      count++;
    } else {
      break;
    }
  }
  return count;
};

/**
 * Count the number of matches in a guess and determine which solution (or alternate)
 * fits the guess best.
 */
export const getBestFit = (
  value: string,
  solution: string,
  alternates: string[],
  strictness?: Strictness,
) => {
  return alternates.reduce((best, next) => {
    const a = getMatches(value, best, strictness);
    const b = getMatches(value, next, strictness);
    return countMatches(a) < countMatches(b) ? next : best;
  }, solution);
};
